import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAdminAuthStore } from '@/stores/auth'

/**
 * Route meta types for admin router.
 * Admin-specific — MUST NOT import from shared/types/.
 */
export type AdminGuard = 'guest-only' | 'auth+mfa-needed' | 'auth+mfa' | 'auth+mfa+role' | 'none'

export interface AdminRouteMeta {
  guard: AdminGuard
  layout: 'AuthLayout' | 'AdminLayout'
  pageTitle?: string
  requiredRoles?: string[]
}

/**
 * Decode a JWT payload without verification (verification happens server-side).
 * Returns null if the token is malformed.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded) as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * Check if the current admin token is valid:
 * - Token exists in the auth store
 * - JWT audience is "admin" (NEVER accept "webapp" tokens)
 * - Token is not expired
 */
function isAuthenticated(): boolean {
  const authStore = useAdminAuthStore()
  const token = authStore.getAccessToken()
  if (!token) return false

  const payload = decodeJwtPayload(token)
  if (!payload) return false

  // SECURITY: Must have aud: "admin" — reject webapp tokens
  if (payload.aud !== 'admin') return false

  // Check expiration
  if (typeof payload.exp === 'number') {
    const now = Math.floor(Date.now() / 1000)
    if (payload.exp <= now) return false
  }

  return true
}

/**
 * Check if the admin user has completed MFA.
 * The backend issues tokens with `mfa_pending: true` when MFA is still needed.
 * A fully authenticated token does NOT have `mfa_pending` — it has `admin_role` + `email`.
 */
function isMfaVerified(): boolean {
  const authStore = useAdminAuthStore()
  const token = authStore.getAccessToken()
  if (!token) return false

  const payload = decodeJwtPayload(token)
  if (!payload) return false

  // If `mfa_pending` is true, MFA is NOT yet complete
  if (payload.mfa_pending === true) return false

  return true
}

/**
 * Get the admin user's role from the JWT claims.
 * Returns the admin_role string or null.
 */
function getAdminRole(): string | null {
  const authStore = useAdminAuthStore()
  const token = authStore.getAccessToken()
  if (!token) return null

  const payload = decodeJwtPayload(token)
  if (!payload) return null

  return typeof payload.admin_role === 'string' ? payload.admin_role : null
}

/**
 * Global navigation guard — runs before every route transition.
 *
 * Guard types:
 *   none          — no checks (404 page)
 *   guest-only    — redirect to dashboard if already authenticated
 *   auth+mfa-needed — only accessible if authenticated but MFA not yet set up
 *   auth+mfa      — requires valid admin JWT + completed MFA
 *   auth+mfa+role — same as auth+mfa + checks admin_role against meta.requiredRoles
 */
export function createAuthGuard() {
  return (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ): void => {
    const meta = to.meta as unknown as AdminRouteMeta | undefined
    const guard: AdminGuard = meta?.guard ?? 'none'

    // Update document title
    if (meta?.pageTitle) {
      document.title = `${meta.pageTitle} | Postlytics Admin`
    } else {
      document.title = 'Postlytics Admin'
    }

    // Guard: none — allow through (e.g., 404)
    if (guard === 'none') {
      next()
      return
    }

    const authenticated = isAuthenticated()
    const mfaComplete = isMfaVerified()

    // Guard: guest-only — redirect authenticated users to dashboard
    if (guard === 'guest-only') {
      if (authenticated && mfaComplete) {
        next({ path: '/admin/dashboard' })
      } else {
        next()
      }
      return
    }

    // Guard: auth+mfa-needed — must be authenticated but MFA NOT yet set up
    if (guard === 'auth+mfa-needed') {
      if (!authenticated) {
        next({ path: '/admin/login' })
        return
      }
      // If MFA is already verified, they don't need setup — send to dashboard
      if (mfaComplete) {
        next({ path: '/admin/dashboard' })
        return
      }
      next()
      return
    }

    // Guards: auth+mfa and auth+mfa+role
    // Both require authentication
    if (!authenticated) {
      next({ path: '/admin/login', query: { redirect: to.fullPath } })
      return
    }

    // Both require MFA completion
    if (!mfaComplete) {
      next({ path: '/admin/mfa/setup' })
      return
    }

    // Guard: auth+mfa+role — additionally check role
    if (guard === 'auth+mfa+role') {
      const requiredRoles = meta?.requiredRoles
      if (requiredRoles && requiredRoles.length > 0) {
        const adminRole = getAdminRole()
        if (!adminRole || !requiredRoles.includes(adminRole)) {
          // Insufficient role — redirect to dashboard, not a 403 page
          next({ path: '/admin/dashboard' })
          return
        }
      }
    }

    next()
  }
}
